import database from './database';
import helper from './helper';

const API_URL = process.env.VUE_APP_API_URL;

function uuid() {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

function headers(token) {
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Authorization': 'Bearer ' + token,
    'X-Kiosk-Version': helper.version,
  };
}

/** Oldest scan first: the server derives in/out from the scan before. */
function byClientTime(a, b) {
  return String(a.client_time).localeCompare(String(b.client_time));
}

export default {
  // Set by main.js so a revoked token can route to the pairing screen.
  router: null,

  // One flush at a time: a scan and the 5-minute timer flushing together
  // could otherwise convert the same legacy row twice.
  flushing: null,

  pairing() {
    return database.getConfig('pairing');
  },

  pair(code) {
    return fetch(API_URL + '/kiosk/pair', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ code: code.trim().toUpperCase(), app_version: helper.version }),
    }).then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          throw new Error((data.errors && data.errors.code && data.errors.code[0]) || 'Koppelen mislukt');
        }
        return Promise.all([
          database.setConfig('pairing', {
            token: data.token,
            slug: data.account.slug,
            account_id: data.account.id,
            account_name: data.account.name,
            device_name: data.device.name,
          }),
          database.setConfig('last_server_contact', data.server_time),
          database.setConfig('revoked', false),
        ]);
      });
  },

  ping() {
    return this.pairing().then(pairing => {
      if (!pairing) {
        return null;
      }
      return fetch(API_URL + '/account/' + pairing.slug + '/kiosk/ping', { headers: headers(pairing.token) })
        .then(res => this.handleAuth(res))
        .then(res => res.json())
        .then(data => database.setConfig('last_server_contact', data.server_time).then(() => data));
    });
  },

  /**
   * Queue first (a scan is never lost), then try to send right away.
   * Resolves with the server's verdict for this scan, or null when offline.
   */
  record(scan) {
    return Promise.all([this.pairing(), database.getConfig('last_server_contact')]).then(([pairing, lastContact]) => {
      const badge = {
        id: uuid(),
        account: scan.account,
        user_id: scan.id,
        name: scan.name,
        client_time: new Date().toISOString(),
        last_server_contact: lastContact,
      };
      badge.uuid = badge.id;

      return database.writeData('sync-badges', badge)
        .then(() => this.flush())
        .then(results => (results || []).find(r => r.uuid === badge.uuid) || null)
        .catch(() => null);
    });
  },

  /**
   * Scans queued by the old kiosk build ({id: <ISO time>, timestamp, account,
   * user_id}, no uuid) are converted, not dropped: keeping them is the whole
   * point of pwa-api answering 410 with no ids.
   */
  normalizeLegacy(queued) {
    return Promise.all(queued.map(b => {
      if (b.uuid || !b.timestamp) {
        return Promise.resolve(b);
      }
      // The old build queued scans without a user id; pwa-api dropped them
      // too. They can never be recorded and would block the queue.
      if (!Number.isInteger(Number(b.user_id)) || b.user_id === null || b.user_id === undefined) {
        console.warn('Dropping a legacy scan without a user id', b);
        return database.deleteItemFromData('sync-badges', b.id).then(() => null);
      }
      const converted = {
        id: uuid(), account: b.account, user_id: b.user_id, name: b.name,
        client_time: b.timestamp, last_server_contact: null,
      };
      converted.uuid = converted.id;
      return database.writeData('sync-badges', converted)
        .then(() => database.deleteItemFromData('sync-badges', b.id))
        .then(() => converted);
    }));
  },

  flush() {
    if (this.flushing) {
      return this.flushing;
    }

    this.flushing = Promise.all([this.pairing(), database.readAllData('sync-badges')]).then(([pairing, raw]) => {
      if (!pairing || raw.length === 0) {
        return [];
      }

      return this.normalizeLegacy(raw).then(queued => this.send(pairing, queued.filter(Boolean)));
    }).finally(() => {
      this.flushing = null;
    });

    return this.flushing;
  },

  send(pairing, queued) {
    const own = queued.filter(b => String(b.account) === String(pairing.account_id));
    queued.filter(b => String(b.account) !== String(pairing.account_id)).forEach(b => {
      console.warn('Dropping a scan for another account', b);
      database.deleteItemFromData('sync-badges', b.id);
    });

    if (own.length === 0) {
      return [];
    }

    const batch = own.slice().sort(byClientTime).slice(0, 100).map(b => ({
      uuid: b.uuid, user_id: b.user_id, client_time: b.client_time, last_server_contact: b.last_server_contact,
    }));

    return fetch(API_URL + '/account/' + pairing.slug + '/kiosk/badge-times', {
      method: 'POST', headers: headers(pairing.token), body: JSON.stringify({ badges: batch }),
    }).then(res => this.handleAuth(res))
      .then(res => res.json())
      .then(data => {
        data.results.forEach(r => database.deleteItemFromData('sync-badges', r.uuid));
        return database.setConfig('last_server_contact', data.server_time).then(() => data.results);
      });
  },

  handleAuth(res) {
    if (res.status === 401) {
      return database.setConfig('revoked', true).then(() => {
        // Straight to the pairing screen; the queue stays in IndexedDB.
        if (this.router && this.router.currentRoute.name !== 'pair') {
          this.router.replace({ name: 'pair' });
        }
        throw new Error('revoked');
      });
    }
    if (!res.ok) {
      throw new Error('HTTP ' + res.status);
    }
    return res;
  },
};
